import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import config from '../../../envConfig';
import { selectCarteiraDocumentos, irParaPreviewImagem } from '../carteiraDocumentosSlice';
import { setArquivoTemp } from '../arquivoTemp';

const FORMATOS_ACEITOS = ['image/png', 'image/jpg', 'image/jpeg'];

export const useUploadCordova = () => {
  const dispatch = useAppDispatch();
  const dados = useAppSelector(selectCarteiraDocumentos);
  const [loading, setLoading] = useState(false);
  const [erroAlerta, setErroAlerta] = useState<string | null>(null);

  const showError = (message: string) => setErroAlerta(message);

  const convertFileURI = (fileURI: string): Promise<string> =>
    new Promise((resolve) => {
      if ((window as any).resolveLocalFileSystemURL) {
        (window as any).resolveLocalFileSystemURL(
          fileURI,
          (entry: any) => resolve(entry.toURL()),
          () => resolve(fileURI)
        );
      } else {
        resolve(fileURI);
      }
    });

  const detectIsPdf = (fileURI: string): Promise<boolean> => {
    if (fileURI.toLowerCase().endsWith('.pdf')) return Promise.resolve(true);

    if (!fileURI.startsWith('content://') || !(window as any).resolveLocalFileSystemURL) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      (window as any).resolveLocalFileSystemURL(
        fileURI,
        (entry: any) => entry.file(
          (file: any) => resolve(file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')),
          () => resolve(false)
        ),
        () => resolve(false)
      );
    });
  };

  const processFile = async (fileURI: string, isPdf = false) => {
    const finalIsPdf = isPdf || await detectIsPdf(fileURI);
    const resolvedURI = await convertFileURI(fileURI);
    setLoading(false);

    try {
      const res = await fetch(resolvedURI);
      const blob = await res.blob();
      const mimeType = (typeof blob.type === 'string' && blob.type.includes('/'))
        ? blob.type
        : (finalIsPdf ? 'application/pdf' : 'image/jpeg');
      const ext = finalIsPdf ? 'pdf' : (mimeType.split('/')[1] || 'jpg');
      const rawName = fileURI.split('/').pop()?.split('?')[0] || '';
      const nomeArquivo = (rawName && rawName.includes('.')) ? rawName : `documento_${Date.now()}.${ext}`;
      console.log('[Cordova] blob size:', blob.size, 'mimeType:', mimeType, 'nomeArquivo:', nomeArquivo);
      if (!FORMATOS_ACEITOS.includes(mimeType)) {
        setLoading(false);
        showError('O formato do arquivo não é permitido. Envie o documento em um dos formatos aceitos: PNG, JPG ou JPEG.');
        return;
      }
      if (blob.size > 2 * 1024 * 1024) {
        setLoading(false);
        showError('O documento escolhido ultrapassa o tamanho máximo permitido de 2 MB. Reduza o tamanho do arquivo e tente novamente.');
        return;
      }
      setArquivoTemp({ blob, nomeArquivo, mimeType, tamanhoBytes: blob.size });
    } catch (err) {
      console.error('[Cordova] erro ao fazer fetch do arquivo local:', err);
      throw new Error('Não foi possível ler o arquivo selecionado. Tente novamente.');
    }

    dispatch(irParaPreviewImagem({ imagemSrc: resolvedURI, isPdf: finalIsPdf }));
  };

  const handleSelecionarGaleria = () => {
    setLoading(true);
    const options = {
      quality: 75,
      destinationType: (window as any).Camera.DestinationType.FILE_URI,
      sourceType: (window as any).Camera.PictureSourceType.PHOTOLIBRARY,
      mediaType: (window as any).Camera.MediaType.PICTURE,
      allowEdit: false,
      encodingType: (window as any).Camera.EncodingType.JPEG,
      targetWidth: 1000,
      targetHeight: 1000,
    };
    (window as any).navigator.camera.getPicture(
      (fileURI: string) => processFile(fileURI),
      (error: string) => { setLoading(false); if (error !== 'Camera cancelled.') showError('Não foi possível acessar a galeria'); },
      options
    );
  };

  const handleTirarFoto = () => {
    setLoading(true);
    const options = {
      quality: 75,
      destinationType: (window as any).Camera.DestinationType.FILE_URI,
      sourceType: (window as any).Camera.PictureSourceType.CAMERA,
      mediaType: (window as any).Camera.MediaType.PICTURE,
      allowEdit: false,
      encodingType: (window as any).Camera.EncodingType.JPEG,
      targetWidth: 1000,
      targetHeight: 1000,
      correctOrientation: true,
    };
    (window as any).navigator.camera.getPicture(
      (fileURI: string) => processFile(fileURI),
      (error: string) => { setLoading(false); if (error !== 'Camera cancelled.') showError('Não foi possível acessar a câmera'); },
      options
    );
  };

  return { loading, handleSelecionarGaleria, handleTirarFoto, erroAlerta, limparErroAlerta: () => setErroAlerta(null) };
};
